import classnames from 'classnames';
import React from 'react';
import { LinkProps } from 'react-router-dom';

import { Props as IconProps } from '../Icons/Icons';

import './Button.scss';

export type Props = React.ButtonHTMLAttributes<HTMLButtonElement> &
  Partial<{
    variant: 'primary' | 'secondary' | 'tertiary';
    size: 'small' | 'default';
    rounded: boolean;
    target: string;
    component: React.FC;
    children: React.ReactNode;
    StartIcon: React.FC<IconProps>;
    EndIcon: React.FC<IconProps>;
    as?: string;
    ref: React.Ref<HTMLButtonElement>;
  }> &
  Partial<LinkProps>;

export const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      className,
      variant = 'primary',
      size = 'default',
      rounded = false,
      StartIcon = null,
      EndIcon = null,
      component = null,
      disabled = false,
      as = '',
      ...rest
    },
    ref
  ) => {
    const Component = component || as || 'button';

    return (
      <Component
        ref={ref}
        aria-disabled={disabled ? true : undefined}
        disabled={disabled ? true : undefined}
        {...rest}
        className={classnames(
          'Button',
          `Button--${variant}`,
          `Button--${size}`,
          {
            'Button--rounded': rounded,
            'Button--has-icon-start': StartIcon !== null,
            'Button--has-icon-end': EndIcon !== null,
          },
          className
        )}
      >
        {StartIcon && <StartIcon className="Button__icon-start" />}
        {children}
        {EndIcon && <EndIcon className="Button__icon-end" />}
      </Component>
    );
  }
);
Button.displayName = 'Button';

export const IconButton = React.forwardRef<HTMLButtonElement, Props>(
  ({ children, className, ...props }, ref) => (
    <Button
      className={classnames('IconButton', className)}
      ref={ref}
      {...props}
    >
      {children}
    </Button>
  )
);
IconButton.displayName = 'IconButton';
